// src/utils/sat/satStructure.js
//
// Single source of truth for the SAT skill tree, per-subcategory study-hour
// multipliers, and the shared scoring helpers used to compute the diagnostic
// subcategory breakdown.
//
// These constants/helpers were extracted verbatim from
// components/admin/dsat/reports/SubcategoryBreakdown.js so that the diagnostic
// report and the (upcoming) SAT plan generator share exactly one implementation.
// Do not change values here without expecting the diagnostic report numbers to
// change — they are intentionally identical to the original component.

/**
 * Fixed SAT skill tree – exactly the units/subcategories used by the report.
 */
export const SAT_STRUCTURE = {
  "Reading & Writing": {
    "Information and Ideas": [
      "Central Ideas and Details",
      "Inferences",
      "Command of Evidence",
    ],
    "Craft and Structure": [
      "Words in Context",
      "Text Structure and Purpose",
      "Cross-Text Connections",
    ],
    "Expression of Ideas": ["Rhetorical Synthesis", "Transitions"],
    "Standard English Conventions": [
      "Boundaries",
      "Form, Structure, and Sense",
    ],
  },
  Math: {
    Algebra: [
      "Linear equations in one variable",
      "Linear equations in two variables",
      "Linear functions",
      "Systems of two linear equations in two variables",
      "Linear inequalities in one or two variables",
    ],
    "Advanced Math": [
      "Equivalent expressions",
      "Nonlinear equations in one variable and systems of equations in two variables",
      "Nonlinear functions",
    ],
    "Problem-Solving and Data Analysis": [
      "Ratios, rates, proportional relationships, and units",
      "Percentages",
      "One-variable data - Distributions and measures of center and spread",
      "Two-variable data - Models and scatterplots",
      "Probability and conditional probability",
      "Inference from sample statistics and margin of error",
      "Evaluating statistical claims - Observational studies and experiments",
    ],
    "Geometry and Trigonometry": [
      "Area and volume",
      "Lines, angles, and triangles",
      "Right triangles and trigonometry",
      "Circles",
    ],
  },
};

export const normalize = (str) =>
  String(str || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

/**
 * Multiplier per subcategory (normalized keys).
 */
export const SUB_MULTIPLIERS = {
  // Reading & Writing – Information and Ideas
  [normalize("Central Ideas and Details")]: 20,
  [normalize("Command of Evidence")]: 20, // Textual multiplier
  [normalize("Inferences")]: 8,

  // Craft and Structure
  [normalize("Words in Context")]: 22,
  [normalize("Text Structure and Purpose")]: 10,
  [normalize("Cross-Text Connections")]: 10,

  // Expression of Ideas
  [normalize("Rhetorical Synthesis")]: 10,
  [normalize("Transitions")]: 10,

  // Standard English Conventions
  [normalize("Boundaries")]: 10,
  [normalize("Form, Structure, and Sense")]: 24,

  // Math – Algebra
  [normalize("Linear equations in one variable")]: 10,
  [normalize("Linear equations in two variables")]: 10,
  [normalize("Linear functions")]: 6,
  [normalize("Systems of two linear equations in two variables")]: 10,
  [normalize("Linear inequalities in one or two variables")]: 10,

  // Math – Advanced Math
  [normalize("Equivalent expressions")]: 10,
  [normalize("Nonlinear equations in one variable and systems of equations in two variables")]:
    8,
  [normalize("Nonlinear functions")]: 8,

  // Math – PSDA
  [normalize("Ratios, rates, proportional relationships, and units")]: 10,
  [normalize("Percentages")]: 8,
  [normalize("One-variable data - Distributions and measures of center and spread")]: 6,
  [normalize("Two-variable data - Models and scatterplots")]: 6,
  [normalize("Probability and conditional probability")]: 8,
  [normalize("Inference from sample statistics and margin of error")]: 6,
  [normalize("Evaluating statistical claims - Observational studies and experiments")]: 6,

  // Math – Geometry & Trig
  [normalize("Area and volume")]: 6,
  [normalize("Lines, angles, and triangles")]: 10,
  [normalize("Right triangles and trigonometry")]: 10,
  [normalize("Circles")]: 8,
};

// correctness logic (Updated for Math decimals)
export function isCorrect(studentAnsRaw, correctAnswerRaw) {
  if (!studentAnsRaw || !correctAnswerRaw) return false;

  const sStr = String(studentAnsRaw).trim().toUpperCase();
  const options = String(correctAnswerRaw).split(',').map(s => s.trim().toUpperCase());

  // 1. Exact String Match
  if (options.includes(sStr)) return true;

  // 2. Numeric Check
  const parseVal = (val) => {
    if (val.includes('/')) {
      const [n, d] = val.split('/');
      return parseFloat(n) / parseFloat(d);
    }
    return parseFloat(val);
  };

  const sNum = parseVal(sStr);
  if (isNaN(sNum)) return false;

  return options.some((opt) => {
    const optNum = parseVal(opt);
    // Margin of error 0.01
    return !isNaN(optNum) && Math.abs(sNum - optNum) < 0.01;
  });
}

export function pct(c, t) {
  if (!t || t === 0) return null;
  return Math.round((c / t) * 100);
}
